import { RefreshCw, Shield, User } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslation } from '@/hooks/useTranslation';
import { getGuardRoster, selectShift } from '@/services/api.client';

/**
 * ShiftSelectionPage — "Who's on duty?" screen for Mode B shared devices.
 * Guard taps their name to start their shift. No login required.
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7
 */
export function ShiftSelectionPage() {
  // Redirect to pairing page if no device session exists
  const hasDeviceSession = !!localStorage.getItem('sg_device_session');
  if (!hasDeviceSession) {
    return <Navigate to="/guard/pair" replace />;
  }

  return <ShiftSelectionContent />;
}

function ShiftSelectionContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [shiftStarted, setShiftStarted] = useState<{
    shiftId: string;
    guardId: string;
    guardName: string;
    gateId: string;
    gateName: string;
    startedAt: string;
  } | null>(null);

  const rosterQuery = useQuery({
    queryKey: ['guard', 'roster'],
    queryFn: getGuardRoster,
    refetchInterval: 30_000,
    retry: (failureCount, error) => {
      // Don't retry auth errors — redirect to pairing instead
      if ((error as { status?: number })?.status === 401) return false;
      return failureCount < 2;
    },
  });

  const shiftMutation = useMutation({
    mutationFn: (guardId: string) => selectShift(guardId),
    onSuccess: async (data) => {
      // Store shift info in sessionStorage so useGuardContext has gate context
      // immediately when the guard dashboard loads.
      try {
        sessionStorage.setItem(
          'securegate-guard-active-shift',
          JSON.stringify({
            shiftId: data.shiftId,
            gateId: data.gateId,
            gateName: data.gateName,
            guardId: data.guardId,
            guardName: data.guardName,
            startedAt: data.startedAt,
          }),
        );
      } catch {
        // sessionStorage unavailable — guard context will derive from API
      }
      // Await the 'me' query refetch so AuthContext has the guard identity
      // with the active shift before we navigate — GuardOrDeviceRoute and
      // useGuardContext both depend on this data being fresh.
      await queryClient.invalidateQueries({ queryKey: ['me'] });

      void navigate('/guard');
    },
    onError: () => {
      setSelectedId(null);
    },
  });

  // If the roster query fails with 401, the device session is invalid —
  // clear it and redirect to the pairing page.
  const rosterError = rosterQuery.error as { status?: number } | null;
  if (rosterError?.status === 401) {
    localStorage.removeItem('sg_device_session');
    return <Navigate to="/guard/pair" replace />;
  }

  const handleSelectGuard = (guardId: string) => {
    setSelectedId(guardId);
    shiftMutation.mutate(guardId);
  };

  const { data, isLoading, isError } = rosterQuery;

  // Shift is active — show confirmation and basic gate info
  if (shiftStarted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg-base)] p-4">
        <div className="w-full max-w-md space-y-6 text-center">
          <div className="flex justify-center">
            <div className="flex size-14 items-center justify-center rounded-full bg-[var(--status-green)]/10">
              <Shield className="size-7 text-[var(--status-green-text)]" />
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">
            {t('guard.shiftSelection.shiftActiveTitle')}
          </h1>
          <p className="text-sm text-[var(--text-secondary)]">
            {t('guard.shiftSelection.onDutyAt')
              .replace('{{guardName}}', shiftStarted.guardName)
              .replace('{{gateName}}', shiftStarted.gateName)}
          </p>
          <p className="text-xs text-[var(--text-tertiary)]">
            {t('guard.shiftSelection.startedLabel')}:{' '}
            {new Date(shiftStarted.startedAt).toLocaleTimeString()}
          </p>
          <div className="pt-4">
            <Button
              variant="outline"
              onClick={() => setShiftStarted(null)}
              className="min-h-[44px]"
            >
              {t('guard.shiftSelection.changeGuard')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bg-base)] p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex justify-center">
            <div className="flex size-14 items-center justify-center rounded-full bg-[var(--cta-primary-bg)]/10">
              <Shield className="size-7 text-[var(--cta-primary-bg)]" />
            </div>
          </div>
          {data && (
            <p className="text-sm text-[var(--text-secondary)]">
              {data.propertyName} — {data.gateName}
            </p>
          )}
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">
            {t('guard.shiftSelection.title')}
          </h1>
          <p className="text-sm text-[var(--text-secondary)]">
            {t('guard.shiftSelection.subtitle')}
          </p>
        </div>

        {/* Guard list */}
        <div className="space-y-2">
          {isLoading && (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          )}

          {isError && (
            <div className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-6 text-center">
              <p className="text-sm text-[var(--text-error)]">
                {t('guard.shiftSelection.loadError')}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => void rosterQuery.refetch()}
              >
                <RefreshCw className="mr-2 size-3" />
                {t('guard.shiftSelection.retry')}
              </Button>
            </div>
          )}

          {data?.guards.length === 0 && (
            <div className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-8 text-center">
              <User className="mx-auto size-10 text-[var(--text-tertiary)] mb-3" />
              <p className="text-sm text-[var(--text-secondary)]">
                {t('guard.shiftSelection.noGuards')}
              </p>
              <p className="text-xs text-[var(--text-tertiary)] mt-1">
                {t('guard.shiftSelection.contactPm')}
              </p>
            </div>
          )}

          {data && data.guards.length > 0 && (
            <div className="space-y-2">
              {data.guards.map((guard) => (
                <button
                  key={guard.id}
                  type="button"
                  onClick={() => handleSelectGuard(guard.id)}
                  disabled={shiftMutation.isPending}
                  className="w-full flex items-center gap-4 rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 text-left transition-colors hover:bg-[var(--bg-surface-raised)] hover:border-[var(--cta-primary-bg)] focus:outline-none focus:ring-2 focus:ring-[var(--cta-primary-bg)] disabled:opacity-50 min-h-[60px]"
                  aria-label={t('guard.shiftSelection.startShiftAria').replace(
                    '{{name}}',
                    guard.fullName,
                  )}
                >
                  <div className="flex size-10 items-center justify-center rounded-full bg-[var(--cta-primary-bg)]/10">
                    <User className="size-5 text-[var(--cta-primary-bg)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-medium text-[var(--text-primary)] truncate">
                      {guard.fullName}
                    </p>
                    {guard.badgeNumber && (
                      <p className="text-xs text-[var(--text-tertiary)]">
                        {t('guard.shiftSelection.badge').replace('{{number}}', guard.badgeNumber)}
                      </p>
                    )}
                  </div>
                  {selectedId === guard.id && shiftMutation.isPending && (
                    <RefreshCw className="size-4 animate-spin text-[var(--cta-primary-bg)]" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
